import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  RetainedLivePriceEntry,
  SubscriptionDemandRecord,
  RefreshTrigger
} from '~/types/realtime-price-cache'
import { makeEntryKey, normalizeSymbol, normalizeExchange } from '~/utils/realtimePriceKey'
import {
  FRESHNESS_THRESHOLD_MS,
  MAX_RETAINED_ENTRIES,
  LRU_EVICTION_COUNT
} from '~/utils/realtimePriceCacheConfig'

export interface PriceData {
  symbol: string;
  exchange: string;
  price: number;
  timestamp: number;
  change24h?: number;
  changePercent24h?: number;
  volume24h?: number;
  high24h?: number;
  low24h?: number;
  baseAsset?: string;
  quoteAsset?: string;
  pairType?: 'spot' | 'futures' | 'margin';
}

export interface PriceHistory {
  [symbol: string]: {
    [exchange: string]: PriceData[];
  };
}

export interface PriceState {
  currentPrices: Map<string, PriceData>;
  priceHistory: PriceHistory;
  lastUpdate: number;
  isConnected: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  activeSubscriptions: Set<string>;
}

export const useRealTimePriceStore = defineStore('realTimePrice', () => {
  // State
  const currentPrices = ref<Map<string, PriceData>>(new Map())
  const priceHistory = ref<PriceHistory>({})
  const lastUpdate = ref<number>(Date.now())
  const isConnected = ref<boolean>(false)
  const connectionStatus = ref<
    'connecting' | 'connected' | 'disconnected' | 'error'
  >('disconnected')
  const activeSubscriptions = ref<Set<string>>(new Set())

  // ── Retained registry (survives trade refreshes) ────────────────────────────
  const retainedRegistry = ref<Map<string, RetainedLivePriceEntry>>(new Map())

  // ── Subscription demand registry (ref-counted) ──────────────────────────────
  const demandRegistry = ref<Map<string, SubscriptionDemandRecord>>(new Map())

  // Caching strategies
  const priceCache = ref<
    Map<string, { data: PriceData; timestamp: number; ttl: number }>
  >(new Map())
  const computedCache = ref<
    Map<string, { data: any; timestamp: number; ttl: number }>
  >(new Map())
  const CACHE_TTL = 5000 // 5 seconds for price cache
  const COMPUTED_CACHE_TTL = 10000 // 10 seconds for computed values

  // Efficient data structures for large datasets
  const priceIndex = ref<Map<string, Set<string>>>(new Map()) // symbol -> Set<exchange>
  const exchangeIndex = ref<Map<string, Set<string>>>(new Map()) // exchange -> Set<symbol>
  const timestampIndex = ref<Map<number, Set<string>>>(new Map()) // timestamp bucket -> Set<key>
  const MAX_HISTORY_PER_SYMBOL = 100
  const MAX_TOTAL_PRICES = 1000
  const TIMESTAMP_BUCKET_SIZE = 1000 // 1 second buckets

  // Computed properties
  const connectedExchanges = computed(() => {
    const exchanges = new Set<string>()
    currentPrices.value.forEach((price) => {
      exchanges.add(price.exchange)
    })
    return Array.from(exchanges)
  })

  const availableSymbols = computed(() => {
    const symbols = new Set<string>()
    currentPrices.value.forEach((price) => {
      symbols.add(price.symbol)
    })
    return Array.from(symbols)
  })

  const totalActivePrices = computed(() => currentPrices.value.size)

  const priceChangeIndicators = computed(() => {
    const indicators = new Map<string, 'up' | 'down' | 'neutral'>()
    currentPrices.value.forEach((price, key) => {
      if (price.changePercent24h) {
        indicators.set(
          key,
          price.changePercent24h > 0
            ? 'up'
            : price.changePercent24h < 0
              ? 'down'
              : 'neutral'
        )
      } else {
        indicators.set(key, 'neutral')
      }
    })
    return indicators
  })

  const priceChangeAlerts = computed(() => {
    const alerts: Array<{
      symbol: string;
      exchange: string;
      type:
        | 'significant_gain'
        | 'significant_loss'
        | 'volatility_spike'
        | 'price_stale';
      message: string;
      severity: 'low' | 'medium' | 'high';
      timestamp: number;
    }> = []

    const now = Date.now()
    const staleThreshold = 30000 // 30 seconds

    currentPrices.value.forEach((price, key) => {
      const [symbol, exchange] = key.split('-')

      if (!symbol || !exchange) return // Skip invalid keys

      // Check for significant price changes (>5% in 24h)
      if (price.changePercent24h) {
        if (price.changePercent24h > 5) {
          alerts.push({
            symbol,
            exchange,
            type: 'significant_gain',
            message: `${symbol} gained ${price.changePercent24h.toFixed(2)}% on ${exchange}`,
            severity: 'medium',
            timestamp: now
          })
        } else if (price.changePercent24h < -5) {
          alerts.push({
            symbol,
            exchange,
            type: 'significant_loss',
            message: `${symbol} lost ${Math.abs(price.changePercent24h).toFixed(2)}% on ${exchange}`,
            severity: 'high',
            timestamp: now
          })
        }
      }

      // Check for stale prices
      if (now - price.timestamp > staleThreshold) {
        alerts.push({
          symbol,
          exchange,
          type: 'price_stale',
          message: `${symbol} price data is stale on ${exchange}`,
          severity: 'medium',
          timestamp: now
        })
      }
    })

    return alerts
  })

  const priceChangeTrends = computed(() => {
    const trends = new Map<
      string,
      {
        direction: 'up' | 'down' | 'sideways';
        strength: 'weak' | 'moderate' | 'strong';
        duration: number;
        momentum: number;
      }
    >()

    currentPrices.value.forEach((price, key) => {
      const history = getPriceHistory(price.symbol, price.exchange)
      if (history.length < 5) return

      // Get last 5 data points
      const recentData = history.slice(-5)
      const prices = recentData.map((p) => p.price)

      // Calculate trend direction and strength
      const firstPrice = prices[0]
      const lastPrice = prices[prices.length - 1]

      if (!firstPrice || !lastPrice) return

      const change = lastPrice - firstPrice
      const changePercent = (change / firstPrice) * 100

      let direction: 'up' | 'down' | 'sideways' = 'sideways'
      let strength: 'weak' | 'moderate' | 'strong' = 'weak'

      if (changePercent > 1) {
        direction = 'up'
        strength = changePercent > 3 ? 'strong' : 'moderate'
      } else if (changePercent < -1) {
        direction = 'down'
        strength = changePercent < -3 ? 'strong' : 'moderate'
      }

      // Calculate momentum (rate of change)
      const momentum = changePercent / recentData.length

      trends.set(key, {
        direction,
        strength,
        duration: recentData.length,
        momentum
      })
    })

    return trends
  })

  const averagePriceBySymbol = computed(() => {
    const averages = new Map<string, number>()
    const symbolGroups = new Map<string, PriceData[]>()

    // Group prices by symbol
    currentPrices.value.forEach((price) => {
      if (!symbolGroups.has(price.symbol)) {
        symbolGroups.set(price.symbol, [])
      }
      symbolGroups.get(price.symbol)!.push(price)
    })

    // Calculate average for each symbol
    symbolGroups.forEach((prices, symbol) => {
      const total = prices.reduce((sum, price) => sum + price.price, 0)
      averages.set(symbol, total / prices.length)
    })

    return averages
  })

  const coinPairsByExchange = computed(() => {
    const pairs = new Map<string, Set<string>>()

    currentPrices.value.forEach((price) => {
      if (!pairs.has(price.exchange)) {
        pairs.set(price.exchange, new Set())
      }
      pairs.get(price.exchange)!.add(price.symbol)
    })

    return pairs
  })

  const exchangesByCoinPair = computed(() => {
    const exchanges = new Map<string, Set<string>>()

    currentPrices.value.forEach((price) => {
      if (!exchanges.has(price.symbol)) {
        exchanges.set(price.symbol, new Set())
      }
      exchanges.get(price.symbol)!.add(price.exchange)
    })

    return exchanges
  })

  const priceSpread = computed(() => {
    const spreads = new Map<
      string,
      { min: number; max: number; spread: number; spreadPercent: number }
    >()

    // Group by symbol
    const symbolGroups = new Map<string, PriceData[]>()
    currentPrices.value.forEach((price) => {
      if (!symbolGroups.has(price.symbol)) {
        symbolGroups.set(price.symbol, [])
      }
      symbolGroups.get(price.symbol)!.push(price)
    })

    // Calculate spread for each symbol
    symbolGroups.forEach((prices, symbol) => {
      if (prices.length > 1) {
        const priceValues = prices.map((p) => p.price).sort((a, b) => a - b)
        const min = priceValues[0]
        const max = priceValues[priceValues.length - 1]

        if (!min || !max) return

        const spread = max - min
        const spreadPercent = (spread / min) * 100

        spreads.set(symbol, { min, max, spread, spreadPercent })
      }
    })

    return spreads
  })

  const formattedPrices = computed(() => {
    const formatted = new Map<
      string,
      {
        price: string;
        change: string;
        changePercent: string;
        volume: string;
        high: string;
        low: string;
        timestamp: string;
      }
    >()

    currentPrices.value.forEach((price, key) => {
      const formatNumber = (num: number, decimals: number = 2) => {
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        }).format(num)
      }

      const formatCurrency = (num: number) => {
        // Smart decimal precision for crypto prices
        let fractionDigits = 2

        // For very small values (like SHIB), use more decimal places
        if (num < 0.01) {
          fractionDigits = 8 // Maximum precision for crypto
        } else if (num < 0.1) {
          fractionDigits = 6 // High precision for small values
        } else if (num < 1) {
          fractionDigits = 4 // Medium precision for sub-dollar values
        } else {
          fractionDigits = 2 // Standard precision for larger values
        }

        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits
        }).format(num)
      }

      const formatPercent = (num: number) => {
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(num / 100)
      }

      const formatTimestamp = (timestamp: number) => {
        return new Date(timestamp).toLocaleTimeString('en-US', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      }

      formatted.set(key, {
        price: formatCurrency(price.price),
        change: price.change24h ? formatCurrency(price.change24h) : 'N/A',
        changePercent: price.changePercent24h
          ? formatPercent(price.changePercent24h)
          : 'N/A',
        volume: price.volume24h ? formatNumber(price.volume24h) : 'N/A',
        high: price.high24h ? formatCurrency(price.high24h) : 'N/A',
        low: price.low24h ? formatCurrency(price.low24h) : 'N/A',
        timestamp: formatTimestamp(price.timestamp)
      })
    })

    return formatted
  })

  const priceChangeSummary = computed(() => {
    const summary = {
      totalUp: 0,
      totalDown: 0,
      totalStable: 0,
      averageChange: 0,
      totalChange: 0
    }

    let totalChange = 0
    let changeCount = 0

    currentPrices.value.forEach((price) => {
      if (price.changePercent24h !== undefined) {
        totalChange += price.changePercent24h
        changeCount++

        if (price.changePercent24h > 0) summary.totalUp++
        else if (price.changePercent24h < 0) summary.totalDown++
        else summary.totalStable++
      }
    })

    summary.totalChange = totalChange
    summary.averageChange = changeCount > 0 ? totalChange / changeCount : 0

    return summary
  })

  // Actions
  const updatePrice = (priceData: PriceData) => {
    // Validate timestamp to ensure data freshness (2-second requirement)
    const now = Date.now()
    const dataAge = now - priceData.timestamp

    if (dataAge > 2000) {
      // 2 seconds
      return false // Reject stale data
    }

    const key = `${priceData.symbol}-${priceData.exchange}`

    // Update the price in the store
    currentPrices.value.set(key, priceData)

    // Update cache expiry
    priceCache.value.set(key, {
      data: priceData,
      timestamp: Date.now(),
      ttl: CACHE_TTL
    })

    // Update indexes for efficient querying
    updatePriceIndexes(key, priceData)

    // Update price history with memory management
    updatePriceHistory(priceData)

    // Persist in retained registry so this price survives trade refreshes
    retainSnapshot(priceData.symbol, priceData.exchange, priceData.price, priceData.timestamp)

    // Cleanup old data if needed
    if (currentPrices.value.size > MAX_TOTAL_PRICES) {
      cleanupOldPrices()
    }

    lastUpdate.value = Date.now()
    return true // Success
  }

  // Helper function to update indexes
  const updatePriceIndexes = (key: string, priceData: PriceData) => {
    // Update symbol index
    if (!priceIndex.value.has(priceData.symbol)) {
      priceIndex.value.set(priceData.symbol, new Set())
    }
    priceIndex.value.get(priceData.symbol)!.add(priceData.exchange)

    // Update exchange index
    if (!exchangeIndex.value.has(priceData.exchange)) {
      exchangeIndex.value.set(priceData.exchange, new Set())
    }
    exchangeIndex.value.get(priceData.exchange)!.add(priceData.symbol)

    // Update timestamp index
    const timestampBucket = Math.floor(
      priceData.timestamp / TIMESTAMP_BUCKET_SIZE
    )
    if (!timestampIndex.value.has(timestampBucket)) {
      timestampIndex.value.set(timestampBucket, new Set())
    }
    timestampIndex.value.get(timestampBucket)!.add(key)
  }

  // Helper function to update price history with memory management
  const updatePriceHistory = (priceData: PriceData) => {
    const symbolHistory = priceHistory.value[priceData.symbol] ?? {}
    priceHistory.value[priceData.symbol] = symbolHistory

    const exchangeHistory = symbolHistory[priceData.exchange] ?? []
    symbolHistory[priceData.exchange] = exchangeHistory

    exchangeHistory.push(priceData)

    // Keep only last MAX_HISTORY_PER_SYMBOL entries per symbol-exchange pair
    if (exchangeHistory.length > MAX_HISTORY_PER_SYMBOL) {
      exchangeHistory.shift()
    }
  }

  // Helper function to cleanup old prices
  const cleanupOldPrices = () => {
    const now = Date.now()
    const cutoffTime = now - 300000 // 5 minutes

    // Find old prices
    const oldKeys: string[] = []
    currentPrices.value.forEach((price, key) => {
      if (price.timestamp < cutoffTime) {
        oldKeys.push(key)
      }
    })

    // Remove old prices (keep newest ones)
    oldKeys.slice(0, oldKeys.length - MAX_TOTAL_PRICES).forEach((key) => {
      currentPrices.value.delete(key)
      removeFromIndexes(key)
    })
  }

  // Enhanced memory management
  const cleanupMemory = () => {
    const now = Date.now()
    const _memoryStats = getMemoryStats()

    // Cleanup old price history
    Object.keys(priceHistory.value).forEach((symbol) => {
      const symbolData = priceHistory.value[symbol]
      if (!symbolData) return

      Object.keys(symbolData).forEach((exchange) => {
        const history = symbolData[exchange]
        if (!history) return

        if (history.length > MAX_HISTORY_PER_SYMBOL) {
          // Keep only recent entries
          const recentHistory = history
            .filter((entry) => now - entry.timestamp < 600000) // 10 minutes
            .slice(-MAX_HISTORY_PER_SYMBOL)
          symbolData[exchange] = recentHistory
        }
      })

      // Remove empty symbol entries
      if (Object.keys(symbolData).length === 0) {
        delete priceHistory.value[symbol]
      }
    })

    // Cleanup old timestamp buckets
    const currentBucket = Math.floor(now / TIMESTAMP_BUCKET_SIZE)
    const oldBuckets: number[] = []
    timestampIndex.value.forEach((keys, bucket) => {
      if (currentBucket - bucket > 60) {
        // Keep only last 60 seconds
        oldBuckets.push(bucket)
      }
    })
    oldBuckets.forEach((bucket) => timestampIndex.value.delete(bucket))

    // Clean up caches
    cleanupCache()

    return getMemoryStats()
  }

  // Get memory usage statistics
  const getMemoryStats = () => {
    const totalPrices = currentPrices.value.size
    const totalHistoryEntries = Object.values(priceHistory.value).reduce(
      (sum, exchanges) => {
        return (
          sum +
          Object.values(exchanges).reduce(
            (sum2, history) => sum2 + history.length,
            0
          )
        )
      },
      0
    )
    const totalIndexEntries =
      priceIndex.value.size +
      exchangeIndex.value.size +
      timestampIndex.value.size
    const totalCacheEntries = priceCache.value.size + computedCache.value.size

    return {
      totalPrices,
      totalHistoryEntries,
      totalIndexEntries,
      totalCacheEntries,
      memoryUsage: {
        currentPrices: totalPrices,
        priceHistory: totalHistoryEntries,
        indexes: totalIndexEntries,
        cache: totalCacheEntries,
        total:
          totalPrices +
          totalHistoryEntries +
          totalIndexEntries +
          totalCacheEntries
      }
    }
  }

  // Cache management functions
  const cleanupCache = () => {
    const now = Date.now()

    // Clean up price cache
    for (const [key, entry] of priceCache.value.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        priceCache.value.delete(key)
      }
    }

    // Clean up computed cache
    for (const [key, entry] of computedCache.value.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        computedCache.value.delete(key)
      }
    }
  }

  const getCachedPrice = (key: string): PriceData | undefined => {
    const entry = priceCache.value.get(key)
    if (entry && Date.now() - entry.timestamp < entry.ttl) {
      return entry.data
    }
    return undefined
  }

  const setCachedPrice = (key: string, data: PriceData) => {
    priceCache.value.set(key, {
      data,
      timestamp: Date.now(),
      ttl: CACHE_TTL
    })
  }

  const getCachedComputed = (key: string): any => {
    const entry = computedCache.value.get(key)
    if (entry && Date.now() - entry.timestamp < entry.ttl) {
      return entry.data
    }
    return undefined
  }

  const setCachedComputed = (key: string, data: any) => {
    computedCache.value.set(key, {
      data,
      timestamp: Date.now(),
      ttl: COMPUTED_CACHE_TTL
    })
  }

  // Helper function to remove from indexes
  const removeFromIndexes = (key: string) => {
    const [symbol, exchange] = key.split('-')
    if (!symbol || !exchange) return

    // Remove from symbol index
    const symbolExchanges = priceIndex.value.get(symbol)
    if (symbolExchanges) {
      symbolExchanges.delete(exchange)
      if (symbolExchanges.size === 0) {
        priceIndex.value.delete(symbol)
      }
    }

    // Remove from exchange index
    const exchangeSymbols = exchangeIndex.value.get(exchange)
    if (exchangeSymbols) {
      exchangeSymbols.delete(symbol)
      if (exchangeSymbols.size === 0) {
        exchangeIndex.value.delete(exchange)
      }
    }

    // Remove from timestamp index
    timestampIndex.value.forEach((keys, bucket) => {
      keys.delete(key)
      if (keys.size === 0) {
        timestampIndex.value.delete(bucket)
      }
    })
  }

  const removePrice = (symbol: string, exchange: string) => {
    const key = `${symbol}-${exchange}`
    currentPrices.value.delete(key)
  }

  const clearPrices = () => {
    currentPrices.value.clear()
    priceHistory.value = {}
  }

  const setConnectionStatus = (
    status: 'connecting' | 'connected' | 'disconnected' | 'error'
  ) => {
    connectionStatus.value = status
    isConnected.value = status === 'connected'
  }

  const addSubscription = (symbol: string, exchange: string) => {
    const key = `${symbol}-${exchange}`
    activeSubscriptions.value.add(key)
  }

  const removeSubscription = (symbol: string, exchange: string) => {
    const key = `${symbol}-${exchange}`
    activeSubscriptions.value.delete(key)
  }

  // ── Retained registry methods ────────────────────────────────────────────────

  /**
   * Create or update a retained entry for symbol+exchange.
   * Called by updatePrice so real WebSocket prices are always preserved
   * across trade refreshes and priceStore resets.
   */
  const retainSnapshot = (
    symbol: string,
    exchange: string,
    price: number,
    timestamp: number = Date.now()
  ) => {
    const entryKey = makeEntryKey(symbol, exchange)
    const now = Date.now()
    const existing = retainedRegistry.value.get(entryKey)

    if (existing) {
      existing.lastKnownPrice = price
      existing.lastUpdatedAt = timestamp
      existing.lastAccessedAt = now
      existing.freshnessStatus = 'fresh'
      existing.staleIndicatorVisible = false
      existing.recoveryAttemptCount = 0
    } else {
      retainedRegistry.value.set(entryKey, {
        entryKey,
        symbol: normalizeSymbol(symbol),
        exchange: normalizeExchange(exchange),
        lastKnownPrice: price,
        lastUpdatedAt: timestamp,
        lastAccessedAt: now,
        freshnessStatus: 'fresh',
        consumerCount: 0,
        isVisible: true,
        recoveryAttemptCount: 0,
        staleIndicatorVisible: false
      })
    }

    // Bounded retention: evict LRU entries when over the cap
    if (retainedRegistry.value.size > MAX_RETAINED_ENTRIES) {
      evictLRU(MAX_RETAINED_ENTRIES - LRU_EVICTION_COUNT)
    }
  }

  /**
   * Return the retained entry for a symbol+exchange pair.
   * Returns null if not found or already evicted.
   */
  const getRetainedPrice = (
    symbol: string,
    exchange: string
  ): RetainedLivePriceEntry | null => {
    const entryKey = makeEntryKey(symbol, exchange)
    const entry = retainedRegistry.value.get(entryKey)
    if (!entry || entry.freshnessStatus === 'evicted') return null
    // Touch LRU timestamp
    entry.lastAccessedAt = Date.now()
    return entry
  }

  /**
   * Reconcile the retained registry with a new set of desired entry keys
   * (latest-refresh-wins).  Entries no longer desired lose their consumer
   * count; entries still desired keep their live price.
   */
  const reconcileSymbols = (
    desiredEntryKeys: string[],
    _trigger: RefreshTrigger = 'other'
  ) => {
    const desiredSet = new Set(desiredEntryKeys)

    retainedRegistry.value.forEach((entry, key) => {
      if (desiredSet.has(key)) {
        entry.consumerCount = Math.max(entry.consumerCount, 1)
        entry.isVisible = true
      } else {
        entry.consumerCount = 0
        entry.isVisible = false
      }
    })
  }

  /**
   * Register a consumer for symbol+exchange (ref-count up).
   * Returns the new refCount.
   */
  const registerDemand = (
    symbol: string,
    exchange: string,
    consumerId: string
  ): number => {
    const demandKey = makeEntryKey(symbol, exchange)
    let demand = demandRegistry.value.get(demandKey)

    if (!demand) {
      demand = {
        demandKey,
        symbol: normalizeSymbol(symbol),
        exchange: normalizeExchange(exchange),
        consumerIds: new Set(),
        refCount: 0,
        subscriptionState: 'idle',
        lastSubscribedAt: 0,
        lastErrorAt: null
      }
      demandRegistry.value.set(demandKey, demand)
    }

    if (!demand.consumerIds.has(consumerId)) {
      demand.consumerIds.add(consumerId)
      demand.refCount++

      if (demand.refCount === 1) {
        demand.subscriptionState = 'subscribing'
        demand.lastSubscribedAt = Date.now()
      } else {
        demand.subscriptionState = 'active'
      }
    }

    // Mirror refCount in the retained registry entry
    const retained = retainedRegistry.value.get(demandKey)
    if (retained) {
      retained.consumerCount = demand.refCount
    }

    return demand.refCount
  }

  /**
   * Release a consumer's demand for symbol+exchange (ref-count down).
   * Returns the new refCount (0 means no more consumers).
   */
  const releaseDemand = (
    symbol: string,
    exchange: string,
    consumerId: string
  ): number => {
    const demandKey = makeEntryKey(symbol, exchange)
    const demand = demandRegistry.value.get(demandKey)
    if (!demand) return 0

    if (demand.consumerIds.has(consumerId)) {
      demand.consumerIds.delete(consumerId)
      demand.refCount = Math.max(0, demand.refCount - 1)

      if (demand.refCount === 0) {
        demand.subscriptionState = 'unsubscribing'
      }
    }

    // Mirror in retained registry
    const retained = retainedRegistry.value.get(demandKey)
    if (retained) {
      retained.consumerCount = demand.refCount
    }

    return demand.refCount
  }

  /** Transition an entry fresh → stale and show the stale indicator */
  const markStale = (symbol: string, exchange: string) => {
    const entry = retainedRegistry.value.get(makeEntryKey(symbol, exchange))
    if (entry && entry.freshnessStatus === 'fresh') {
      entry.freshnessStatus = 'stale'
      entry.staleIndicatorVisible = true
    }
  }

  /** Transition a stale entry to recovering state */
  const markRecovering = (symbol: string, exchange: string) => {
    const entry = retainedRegistry.value.get(makeEntryKey(symbol, exchange))
    if (entry && (entry.freshnessStatus === 'stale' || entry.freshnessStatus === 'recovering')) {
      entry.freshnessStatus = 'recovering'
      entry.recoveryAttemptCount++
    }
  }

  /** Mark a recovered entry as fresh again */
  const markFresh = (symbol: string, exchange: string) => {
    const entry = retainedRegistry.value.get(makeEntryKey(symbol, exchange))
    if (entry) {
      entry.freshnessStatus = 'fresh'
      entry.staleIndicatorVisible = false
      entry.recoveryAttemptCount = 0
    }
  }

  /**
   * LRU eviction – keep the `maxEntries` most-recently-accessed entries.
   * Evicted entries are removed from the map (callers that held a reference
   * will not see new updates).
   */
  const evictLRU = (maxEntries: number) => {
    if (retainedRegistry.value.size <= maxEntries) return

    const sorted = Array.from(retainedRegistry.value.entries()).sort(
      (a, b) => a[1].lastAccessedAt - b[1].lastAccessedAt
    )

    const toEvict = sorted.slice(0, sorted.length - maxEntries)
    toEvict.forEach(([key, entry]) => {
      entry.freshnessStatus = 'evicted'
      retainedRegistry.value.delete(key)
    })
  }

  /**
   * Scan retained entries and transition any that have exceeded the freshness
   * threshold to 'stale'.  Designed to be called by a periodic background tick.
   */
  const checkAndMarkStaleEntries = () => {
    const now = Date.now()
    retainedRegistry.value.forEach((entry) => {
      if (
        entry.freshnessStatus === 'fresh' &&
        now - entry.lastUpdatedAt > FRESHNESS_THRESHOLD_MS
      ) {
        entry.freshnessStatus = 'stale'
        entry.staleIndicatorVisible = true
      }
    })
  }

  /** Return all retained entry keys whose status is stale or recovering */
  const getStaleEntryKeys = (): string[] => {
    return Array.from(retainedRegistry.value.values())
      .filter(
        (e) =>
          e.freshnessStatus === 'stale' || e.freshnessStatus === 'recovering'
      )
      .map((e) => e.entryKey)
  }

  /** Return the demand record for a symbol+exchange pair, or null */
  const getDemandRecord = (
    symbol: string,
    exchange: string
  ): SubscriptionDemandRecord | null => {
    return demandRegistry.value.get(makeEntryKey(symbol, exchange)) ?? null
  }

  const getPrice = (
    symbol: string,
    exchange: string
  ): PriceData | undefined => {
    const key = `${symbol}-${exchange}`
    const cached = getCachedPrice(key)
    if (cached) {
      return cached
    }
    const price = currentPrices.value.get(key)
    if (price) {
      setCachedPrice(key, price)
    }
    return price
  }

  const getPriceHistory = (symbol: string, exchange: string): PriceData[] => {
    return priceHistory.value[symbol]?.[exchange] || []
  }

  const getPriceTrend = (
    symbol: string,
    exchange: string,
    timeWindowMs: number = 60000
  ): {
    trend: 'up' | 'down' | 'stable';
    change: number;
    changePercent: number;
    volatility: number;
    dataPoints: number;
  } => {
    const history = getPriceHistory(symbol, exchange)
    const now = Date.now()
    const windowStart = now - timeWindowMs

    // Filter data points within the time window
    const recentData = history.filter((data) => data.timestamp >= windowStart)

    if (recentData.length < 2) {
      return {
        trend: 'stable',
        change: 0,
        changePercent: 0,
        volatility: 0,
        dataPoints: recentData.length
      }
    }

    // Sort by timestamp
    recentData.sort((a, b) => a.timestamp - b.timestamp)

    const firstPrice = recentData[0]!.price
    const lastPrice = recentData[recentData.length - 1]!.price
    const change = lastPrice - firstPrice
    const changePercent = (change / firstPrice) * 100

    // Calculate volatility (standard deviation of price changes)
    const priceChanges = recentData.slice(1).map((data, index) => {
      const prevPrice = recentData[index]!.price
      return ((data.price - prevPrice) / prevPrice) * 100
    })

    const avgChange =
      priceChanges.reduce((sum, change) => sum + change, 0) /
      priceChanges.length
    const variance =
      priceChanges.reduce(
        (sum, change) => sum + Math.pow(change - avgChange, 2),
        0
      ) / priceChanges.length
    const volatility = Math.sqrt(variance)

    let trend: 'up' | 'down' | 'stable' = 'stable'
    if (changePercent > 0.1) trend = 'up'
    else if (changePercent < -0.1) trend = 'down'

    return {
      trend,
      change,
      changePercent,
      volatility,
      dataPoints: recentData.length
    }
  }

  const getPriceStats = (
    symbol: string,
    exchange: string,
    timeWindowMs: number = 60000
  ): {
    min: number;
    max: number;
    avg: number;
    median: number;
    dataPoints: number;
  } => {
    const history = getPriceHistory(symbol, exchange)
    const now = Date.now()
    const windowStart = now - timeWindowMs

    const recentData = history.filter((data) => data.timestamp >= windowStart)

    if (recentData.length === 0) {
      return {
        min: 0,
        max: 0,
        avg: 0,
        median: 0,
        dataPoints: 0
      }
    }

    const prices = recentData.map((data) => data.price).sort((a, b) => a - b)
    const min = prices[0] ?? 0
    const max = prices[prices.length - 1] ?? 0
    const avg = prices.reduce((sum, price) => sum + price, 0) / prices.length
    const median =
      prices.length % 2 === 0
        ? ((prices[prices.length / 2 - 1] ?? 0) + (prices[prices.length / 2] ?? 0)) / 2
        : (prices[Math.floor(prices.length / 2)] ?? 0)

    return {
      min,
      max,
      avg,
      median,
      dataPoints: recentData.length
    }
  }

  const getLatestPrice = (symbol: string): PriceData | undefined => {
    let latestPrice: PriceData | undefined
    let latestTimestamp = 0

    currentPrices.value.forEach((price) => {
      if (price.symbol === symbol && price.timestamp > latestTimestamp) {
        latestPrice = price
        latestTimestamp = price.timestamp
      }
    })

    return latestPrice
  }

  const getAveragePrice = (symbol: string): number | undefined => {
    return averagePriceBySymbol.value.get(symbol)
  }

  const getPriceChange = (
    symbol: string,
    exchange: string
  ): number | undefined => {
    const price = getPrice(symbol, exchange)
    return price?.changePercent24h
  }

  const isPriceStale = (
    symbol: string,
    exchange: string,
    maxAgeMs: number = 30000
  ): boolean => {
    const price = getPrice(symbol, exchange)
    if (!price) return true

    const now = Date.now()
    return now - price.timestamp > maxAgeMs
  }

  const getStalePrices = (maxAgeMs: number = 30000): string[] => {
    const staleKeys: string[] = []
    const now = Date.now()

    currentPrices.value.forEach((price, key) => {
      if (now - price.timestamp > maxAgeMs) {
        staleKeys.push(key)
      }
    })

    return staleKeys
  }

  const cleanupStalePrices = (maxAgeMs: number = 30000) => {
    const staleKeys = getStalePrices(maxAgeMs)
    staleKeys.forEach((key) => {
      currentPrices.value.delete(key)
    })
  }

  return {
    // State
    currentPrices,
    priceHistory,
    lastUpdate,
    isConnected,
    connectionStatus,
    activeSubscriptions,

    // Retained registry state (read-only ref for watchers)
    retainedRegistry,
    demandRegistry,

    // Computed
    connectedExchanges,
    availableSymbols,
    totalActivePrices,
    priceChangeIndicators,
    averagePriceBySymbol,
    coinPairsByExchange,
    exchangesByCoinPair,
    priceSpread,
    formattedPrices,
    priceChangeSummary,
    priceChangeAlerts,
    priceChangeTrends,

    // Actions (existing)
    updatePrice,
    removePrice,
    clearPrices,
    setConnectionStatus,
    addSubscription,
    removeSubscription,
    getPrice,
    getPriceHistory,
    getPriceTrend,
    getPriceStats,
    getLatestPrice,
    getAveragePrice,
    getPriceChange,
    isPriceStale,
    getStalePrices,
    cleanupStalePrices,
    getMemoryStats,
    cleanupMemory,
    // Cache functions
    getCachedPrice,
    setCachedPrice,
    getCachedComputed,
    setCachedComputed,
    cleanupCache,

    // Retained registry actions (new)
    retainSnapshot,
    getRetainedPrice,
    reconcileSymbols,
    registerDemand,
    releaseDemand,
    markStale,
    markFresh,
    markRecovering,
    evictLRU,
    checkAndMarkStaleEntries,
    getStaleEntryKeys,
    getDemandRecord
  }
})
